<template>
  <HrPageLayout
    title="My Tax Information"
    subtitle="View tax summaries and download P9 forms"
    icon="📋"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/employee-dashboard' },
      { label: 'Tax' }
    ]"
  >
    <!-- Loading State -->
    <div v-if="loading" class="hr-loading-wrap">
      <div class="hr-spinner"></div>
      <p>Loading tax information...</p>
    </div>

    <div v-else-if="taxData" class="tax-content">
      <!-- Year Selector & Actions -->
      <div class="hr-content-grid hr-mb-4">
        <div class="hr-chart-card">
          <div class="hr-chart-body">
            <label class="hr-form-label">Select Tax Year</label>
            <select v-model="selectedYear" @change="loadTaxSummary" class="hr-select">
              <option v-for="year in taxData.availableYears" :key="year" :value="year">{{ year }}</option>
            </select>
          </div>
        </div>
        <div class="hr-chart-card">
          <div class="hr-chart-body hr-flex hr-justify-end hr-align-center">
            <button @click="downloadP9Form" class="hr-btn hr-btn-success hr-me-2">
              <i class="bx bx-download hr-me-1"></i>Download P9 Form
            </button>
            <button @click="loadTaxSummary" class="hr-btn hr-btn-secondary">
              <i class="bx bx-refresh hr-me-1"></i>Refresh
            </button>
          </div>
        </div>
      </div>

      <!-- Tax Summary Cards -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-money"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Gross Income</p>
          <h2 class="hr-metric-value">KES {{ formatCurrency(taxData.taxSummary.totalGross) }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-calculator"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Taxable Income</p>
          <h2 class="hr-metric-value">KES {{ formatCurrency(taxData.taxSummary.totalTaxable) }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-money"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total PAYE</p>
          <h2 class="hr-metric-value">KES {{ formatCurrency(taxData.taxSummary.totalPaye) }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-purchase-tag"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Effective Tax Rate</p>
          <h2 class="hr-metric-value">{{ taxData.taxSummary.effectiveTaxRate }}%</h2>
            </div>
        </div>
      </div>

      <!-- Tax Breakdown & Reliefs -->
      <div class="hr-content-grid hr-mb-4">
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-file hr-me-2 hr-text-danger"></i>Tax Breakdown</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="taxData.taxes && taxData.taxes.length > 0">
              <div v-for="(tax, index) in taxData.taxes" :key="index" class="hr-flex hr-justify-between hr-align-center hr-mb-3 hr-pb-3 hr-border-bottom">
                <div>
                  <h6 class="hr-mb-1">{{ tax.component_name }}</h6>
                  <p class="hr-text-muted hr-mb-0 hr-small">{{ tax.occurrence_count }} occurrences</p>
                </div>
                <h5 class="hr-mb-0 hr-text-danger">KES {{ formatCurrency(tax.total_amount) }}</h5>
              </div>
            </div>
            <div v-else class="hr-empty-state">
              <div class="hr-empty-icon"><i class="bx bx-file"></i></div>
              <p class="hr-mb-0">No tax data available</p>
            </div>
          </div>
        </div>

        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-purchase-tag hr-me-2 hr-text-success"></i>Tax Reliefs</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="taxData.reliefs && taxData.reliefs.length > 0">
              <div v-for="(relief, index) in taxData.reliefs" :key="index" class="hr-flex hr-justify-between hr-align-center hr-mb-3 hr-pb-3 hr-border-bottom">
                <div>
                  <h6 class="hr-mb-1">{{ relief.component_name }}</h6>
                  <p class="hr-text-muted hr-mb-0 hr-small">{{ relief.occurrence_count }} occurrences</p>
                </div>
                <h5 class="hr-mb-0 hr-text-success">KES {{ formatCurrency(Math.abs(relief.total_amount)) }}</h5>
              </div>
              <div class="hr-bg-success-subtle hr-p-3 hr-rounded hr-mt-3">
                <div class="hr-flex hr-justify-between">
                  <strong>Total Reliefs:</strong>
                  <strong class="hr-text-success">KES {{ formatCurrency(taxData.taxSummary.totalReliefs) }}</strong>
                </div>
              </div>
            </div>
            <div v-else class="hr-empty-state">
              <div class="hr-empty-icon"><i class="bx bx-purchase-tag"></i></div>
              <p class="hr-mb-0">No relief data available</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Statutory Deductions -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-shield-quarter hr-me-2"></i>Statutory Deductions</h5>
        </div>
        <div class="hr-chart-body">
          <div v-if="taxData.statutoryDeductions && taxData.statutoryDeductions.length > 0" class="hr-deductions-grid">
            <div v-for="(deduction, index) in taxData.statutoryDeductions" :key="index" class="hr-deduction-card">
              <div class="hr-flex hr-align-center">
                <div class="hr-avatar-xs hr-bg-primary hr-me-3">
                  <i class="bx bx-shield-check"></i>
                </div>
                <div class="hr-flex-1">
                  <h6 class="hr-mb-1">{{ deduction.component_name }}</h6>
                  <p class="hr-text-muted hr-mb-0 hr-small">{{ deduction.occurrence_count }} payments</p>
                </div>
              </div>
              <div class="hr-mt-3">
                <h4 class="hr-mb-0">KES {{ formatCurrency(deduction.total_amount) }}</h4>
              </div>
            </div>
          </div>
          <div v-else class="hr-empty-state">
            <div class="hr-empty-icon"><i class="bx bx-shield"></i></div>
            <p class="hr-mb-0">No statutory deductions found</p>
          </div>
        </div>
      </div>

      <!-- Monthly Breakdown -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-line-chart hr-me-2"></i>Monthly Tax Breakdown - {{ selectedYear }}</h5>
        </div>
        <div class="hr-chart-body">
          <div v-if="taxData.monthlyBreakdown && taxData.monthlyBreakdown.length > 0" class="hr-table-wrapper">
            <table class="hr-table hr-table-hover hr-table-sm">
              <thead>
              <tr>
                <th>Month</th>
                <th class="hr-text-end">Gross Salary</th>
                <th class="hr-text-end">Taxable Income</th>
                <th class="hr-text-end">PAYE</th>
                <th class="hr-text-end">Net Salary</th>
                <th class="hr-text-end">Tax Rate</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(month, index) in taxData.monthlyBreakdown" :key="index">
                <td><strong>{{ month.month_name }}</strong></td>
                <td class="hr-text-end">{{ formatCurrency(month.gross_salary) }}</td>
                <td class="hr-text-end">{{ formatCurrency(month.taxable_income) }}</td>
                <td class="hr-text-end hr-text-danger"><strong>{{ formatCurrency(month.paye) }}</strong></td>
                <td class="hr-text-end hr-text-success"><strong>{{ formatCurrency(month.net_salary) }}</strong></td>
                <td class="hr-text-end">
                  <span class="hr-badge hr-badge-info">{{ calculateTaxRate(month.paye, month.gross_salary) }}%</span>
                </td>
              </tr>
              </tbody>
              <tfoot class="hr-table-foot">
              <tr>
                <td><strong>TOTAL</strong></td>
                <td class="hr-text-end">{{ formatCurrency(getTotalColumn('gross_salary')) }}</td>
                <td class="hr-text-end">{{ formatCurrency(getTotalColumn('taxable_income')) }}</td>
                <td class="hr-text-end hr-text-danger">{{ formatCurrency(getTotalColumn('paye')) }}</td>
                <td class="hr-text-end hr-text-success">{{ formatCurrency(getTotalColumn('net_salary')) }}</td>
                <td class="hr-text-end">
                  <span class="hr-badge hr-badge-primary">{{ taxData.taxSummary.effectiveTaxRate }}%</span>
                </td>
              </tr>
              </tfoot>
            </table>
          </div>
          <div v-else class="hr-empty-state">
            <div class="hr-empty-icon"><i class="bx bx-line-chart"></i></div>
            <p class="hr-mb-0">No monthly data available for {{ selectedYear }}</p>
          </div>
        </div>
      </div>

      <!-- KRA Information -->
      <div class="hr-chart-card hr-border-primary">
        <div class="hr-chart-body">
          <div class="hr-flex hr-flex-wrap hr-justify-between hr-align-center">
            <div class="hr-flex-1 hr-min-w-200">
              <h5 class="hr-text-primary hr-mb-2">
                <i class="bx bx-info-circle hr-me-2"></i>KRA Information
              </h5>
              <div class="hr-flex hr-flex-wrap hr-gap-4">
                <div>
                  <p class="hr-text-muted hr-mb-1 hr-small">Your KRA PIN</p>
                  <h6 class="hr-mb-0">{{ taxData.employee.kraPin || 'Not Set' }}</h6>
                </div>
                <div>
                  <p class="hr-text-muted hr-mb-1 hr-small">Employee Number</p>
                  <h6 class="hr-mb-0">{{ taxData.employee.empNo }}</h6>
                </div>
              </div>
            </div>
            <div class="hr-mt-3 hr-mt-md-0">
              <a href="https://itax.kra.go.ke/KRA-Portal/" target="_blank" rel="noopener" class="hr-btn hr-btn-primary">
                <i class="bx bx-link-external hr-me-1"></i>Visit iTax Portal
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeePayrollTax",
  components: { HrPageLayout },

  data() {
    return {
      loading: false,
      taxData: null,
      selectedYear: new Date().getFullYear()
    };
  },

  mounted() {
    this.loadTaxSummary();
  },

  methods: {
    async loadTaxSummary() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getEmployeeTaxSummary");
        data.append("year", this.selectedYear);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.taxData = res.data.data;
        } else {
          throw new Error(res.data?.message || 'Failed to load tax summary');
        }
      } catch (error) {
        this.$toast?.error("Failed to load tax summary: " + error.message) || console.error("Failed to load tax summary:", error);
      } finally {
        this.loading = false;
      }
    },

    async downloadP9Form() {
      try {
        this.$toast?.info("Generating P9 form...") || console.log("Generating P9 form...");
        const data = new FormData();
        data.append("function", "getEmployeeP9Form");
        data.append("year", this.selectedYear);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.generateP9PDF(res.data.data);
        } else {
          throw new Error(res.data?.message || 'Failed to generate P9 form');
        }
      } catch (error) {
        this.$toast?.error("Failed to download P9 form: " + error.message) || alert("Failed to download P9 form: " + error.message);
      }
    },

    async generateP9PDF(p9Data) {
      try {
        const jsPDF = (await import('jspdf')).default;
        const autoTable = (await import('jspdf-autotable')).default;
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;

        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text('P9 TAX DEDUCTION CARD', pageWidth / 2, 20, { align: 'center' });
        doc.setFontSize(12);
        doc.setFont(undefined, 'normal');
        doc.text(`Year of Income: ${p9Data.year}`, pageWidth / 2, 28, { align: 'center' });

        let startY = 35;
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Employee Information:', 15, startY);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        doc.text(`Name: ${p9Data.employee.fullName}`, 15, startY + 6);
        doc.text(`PIN: ${p9Data.employee.kraPin}`, 15, startY + 12);
        doc.text(`Employee No: ${p9Data.employee.empNo}`, 15, startY + 18);

        doc.setFont(undefined, 'bold');
        doc.setFontSize(10);
        doc.text('Employer Information:', pageWidth / 2 + 10, startY);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        doc.text(`Name: ${p9Data.employer.name}`, pageWidth / 2 + 10, startY + 6);
        doc.text(`PIN: ${p9Data.employer.pin}`, pageWidth / 2 + 10, startY + 12);
        startY += 30;

        const tableData = p9Data.monthlyData.map(month => [
          month.month_name, this.formatAmount(month.basic_salary), this.formatAmount(month.benefits),
          this.formatAmount(month.total_gross), this.formatAmount(month.nssf), this.formatAmount(month.nhif),
          this.formatAmount(month.housing_levy), this.formatAmount(month.taxable_income),
          this.formatAmount(month.tax_charged), this.formatAmount(month.personal_relief), this.formatAmount(month.paye)
        ]);
        tableData.push([
          'TOTAL', this.formatAmount(p9Data.totals.basic_salary), this.formatAmount(p9Data.totals.benefits),
          this.formatAmount(p9Data.totals.total_gross), this.formatAmount(p9Data.totals.nssf),
          this.formatAmount(p9Data.totals.nhif), this.formatAmount(p9Data.totals.housing_levy),
          this.formatAmount(p9Data.totals.taxable_income), this.formatAmount(p9Data.totals.tax_charged),
          this.formatAmount(p9Data.totals.personal_relief), this.formatAmount(p9Data.totals.paye)
        ]);

        autoTable(doc, {
          startY,
          head: [[ 'Month', 'Basic', 'Benefits', 'Gross', 'NSSF', 'NHIF/SHIF', 'H.Levy', 'Taxable', 'Tax', 'Relief', 'PAYE' ]],
          body: tableData,
          theme: 'grid',
          headStyles: { fillColor: [66, 139, 202], fontSize: 7, fontStyle: 'bold' },
          bodyStyles: { fontSize: 7 },
          columnStyles: {
            0: { cellWidth: 18, fontStyle: 'bold' },
            1: { cellWidth: 16, halign: 'right' }, 2: { cellWidth: 16, halign: 'right' },
            3: { cellWidth: 16, halign: 'right' }, 4: { cellWidth: 14, halign: 'right' },
            5: { cellWidth: 14, halign: 'right' }, 6: { cellWidth: 14, halign: 'right' },
            7: { cellWidth: 18, halign: 'right' }, 8: { cellWidth: 16, halign: 'right' },
            9: { cellWidth: 16, halign: 'right' }, 10: { cellWidth: 16, halign: 'right', fontStyle: 'bold' }
          },
          didParseCell: (data) => {
            if (data.row.index === tableData.length - 1) {
              data.cell.styles.fillColor = [240, 240, 240];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        });

        doc.save(`P9_${p9Data.employee.empNo}_${p9Data.year}.pdf`);
        this.$toast?.success("P9 form downloaded successfully") || alert("P9 form downloaded successfully");
      } catch (error) {
        console.error('Error generating P9 PDF:', error);
        this.$toast?.error("Error generating PDF: " + error.message) || alert("Error generating PDF: " + error.message);
      }
    },

    formatCurrency(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatAmount(value) {
      return new Intl.NumberFormat('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);
    },
    calculateTaxRate(paye, gross) {
      if (!gross || gross === 0) return '0.0';
      return ((paye / gross) * 100).toFixed(1);
    },
    getTotalColumn(columnName) {
      if (!this.taxData || !this.taxData.monthlyBreakdown) return 0;
      return this.taxData.monthlyBreakdown.reduce((sum, month) => sum + parseFloat(month[columnName] || 0), 0);
    },
    gotTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.hr-deductions-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 16px; }
.hr-deduction-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; }
.hr-avatar-xs { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 16px; }
.hr-bg-primary { background: linear-gradient(135deg, #5b73e8, #364574); color: white; }
.hr-bg-success-subtle { background: rgba(40, 167, 69, 0.1); }
.hr-rounded { border-radius: 12px; }
.hr-border-primary { border: 2px solid #5b73e8; }
.hr-table-foot { background: #f8fafc; font-weight: 600; }
.hr-me-2 { margin-right: 8px; }
.hr-me-3 { margin-right: 12px; }
.hr-min-w-200 { min-width: 200px; }
.hr-mt-md-0 { margin-top: 0; }
@media (min-width: 768px) { .hr-mt-md-0 { margin-top: 0; } }
@media (max-width: 767px) { .hr-mt-md-0 { margin-top: 12px; } }
</style>
